/**
 * Registro de execuções de ferramentas na API
 *
 * Contrato com Backend:
 * - Autenticação obrigatória via JWT/SSO Token (header Authorization)
 * - Backend extrai userId automaticamente do token (NÃO enviar no body)
 * - Campos obrigatórios: requestId, status, toolSlug
 * - Formato de métricas: "Convertidos=X; Erros=Y; Total=Z; Tempo=Ns; Média=Ms/obj"
 * - Tratamento de erros: 401 (renovar token), 400 (corrigir payload)
 */

import { fetchWithSsoRetry, getBaseBearer } from "./toolsSso";
import { API_URL } from "../config";
import { sendToTerminal, logger } from "./terminalLogger";

/**
 * Resposta do vbcompiler/host C#
 */
interface ExecutionResponse {
	requestId: string;
	status: string; // 'ok' | 'erro' | 'error' | 'timeout'
	result?: any; // Objeto ou string retornada pelo VB
	message?: string; // Mensagem de erro se status != 'ok'
}

/**
 * Dados da ferramenta (do backend)
 */
interface ToolData {
	slug: string;
	id?: string;
	[key: string]: any;
}

/**
 * Resposta da API de registro
 */
interface RegisterExecutionResult {
	logId: string;
	message?: string;
}

/**
 * Extrai métricas do retorno do VB para formato padronizado
 *
 * Exemplos de entrada:
 * - "Convertidos=12; Erros=0; Total=12; Tempo=5.2s; Média=0.43s/obj"
 * - { message: "ok", details: "..." }
 * - "ok"
 *
 * Saída esperada: string no formato "Convertidos=X; Erros=Y; Total=Z; Tempo=Ns; Média=Ms/obj"
 */
function formatRetorno(result: any): string {
	// Se já é string no formato esperado, retornar direto
	if (typeof result === "string") {
		return result;
	}

	// Se é objeto, tentar extrair informações
	if (typeof result === "object" && result !== null) {
		// Verificar se tem retorno ou message
		if (result.retorno) {
			return String(result.retorno);
		}
		if (result.message) {
			return String(result.message);
		}

		// Tentar construir formato de métricas se tiver campos conhecidos
		if (result.convertidos !== undefined || result.total !== undefined) {
			const convertidos = result.convertidos ?? result.ok ?? 0;
			const erros = result.erros ?? result.errors ?? 0;
			const total = result.total ?? convertidos + erros;
			const tempo = result.tempo ?? result.time ?? "0s";
			const media = result.media ?? result.avg ?? "0s/obj";

			return `Convertidos=${convertidos}; Erros=${erros}; Total=${total}; Tempo=${tempo}; Média=${media}`;
		}

		// Fallback: stringificar
		return JSON.stringify(result);
	}

	// Fallback final: converter para string
	return String(result);
}

/**
 * Registra execução de ferramenta na API
 *
 * @param response - Resposta do vbcompiler/host C#
 * @param toolData - Dados da ferramenta (inclui slug)
 * @param parameters - Parâmetros usados na execução
 * @returns Promise com { logId } ou null em caso de erro
 *
 * @example
 * ```typescript
 * // Registro de sucesso
 * registerExecution(
 *   { requestId: 'req-123', status: 'ok', result: { message: 'Convertidos=10' } },
 *   { slug: 'converter-para-bitmap' },
 *   { dpi: 300, colorMode: 'CMYK' }
 * );
 *
 * // Registro de erro
 * registerExecution(
 *   { requestId: 'req-124', status: 'error', message: 'Nenhuma seleção' },
 *   { slug: 'converter-para-bitmap' },
 *   { dpi: 300, colorMode: 'CMYK' }
 * );
 * ```
 */
export async function registerExecution(
	response: ExecutionResponse,
	toolData: ToolData,
	parameters: Record<string, any>,
): Promise<RegisterExecutionResult | null> {
	try {
		// Validação básica dos campos obrigatórios
		if (!response.requestId || !response.status || !toolData.slug) {
			try {
				sendToTerminal("⚠️ [REGISTER_EXECUTION:VALIDATION_ERROR]", {
					hasRequestId: !!response.requestId,
					hasStatus: !!response.status,
					hasSlug: !!toolData.slug,
					error: "Campos obrigatórios ausentes",
				});
			} catch {}
			return null;
		}

		// Preparar retorno formatado
		const retorno =
			response.status === "ok"
				? formatRetorno(response.result)
				: response.message || "Erro desconhecido";

		// Mensagem resumida para o campo message (obrigatório pelo backend)
		const message =
			response.status === "ok"
				? "Execução concluída com sucesso"
				: response.message || "Erro na execução";

		// Payload seguindo o contrato do backend
		const payload = {
			// Campos obrigatórios
			requestId: response.requestId,
			status: response.status,
			toolSlug: toolData.slug,

			// Result com message (obrigatório) e retorno (métricas)
			result: {
				message: message,
				retorno: retorno,
			},

			// NÃO enviar userId - backend extrai do token automaticamente
		};

		// Log resumido sem payload completo
		try {
			sendToTerminal("📤 [REGISTER_EXECUTION] Registrando execução", {
				requestId: payload.requestId,
				status: payload.status,
				toolSlug: payload.toolSlug,
				messageLength: message.length,
				retornoLength: retorno.length,
			});
		} catch {}

		// Endpoint conforme documentação
		const endpoint = `${API_URL}/tools/executions`;

		// Log resumido sem body completo
		const bodyString = JSON.stringify(payload);

		// Usar fetchWithSsoRetry para tratamento automático de 401
		const res = await fetchWithSsoRetry(
			endpoint,
			{
				method: "POST",
				headers: {
					"Content-Type": "application/json",
					// Authorization é injetado automaticamente pelo fetchWithSsoRetry
				},
				body: bodyString,
			},
			getBaseBearer(),
		);

		// Tratamento de erros HTTP
		if (!res.ok) {
			const errorText = await res.text().catch(() => "Erro desconhecido");
			let errorDetails = null;

			// Tentar parsear erro como JSON
			try {
				errorDetails = JSON.parse(errorText);
			} catch {
				errorDetails = { rawError: errorText };
			}

			// 400 = erro de payload (problema no código)
			if (res.status === 400) {
				try {
					sendToTerminal("❌ [REGISTER_EXECUTION:BAD_REQUEST]", {
						status: res.status,
						error: errorText,
						errorDetails: errorDetails,
						sentPayload: payload,
						sentPayloadJSON: JSON.stringify(payload, null, 2),
						hint: "Verificar formato do payload - campos obrigatórios ou formato incorreto",
					});

					// Log separado com comparação do que foi enviado vs o que era esperado
					if (errorDetails?.details) {
						sendToTerminal("🔍 [REGISTER_EXECUTION:VALIDATION_ERRORS]", {
							validationErrors: errorDetails.details,
							fieldsWithErrors: errorDetails.details.map(
								(d: any) => d.field || d.path,
							),
							sentFields: Object.keys(payload),
							sentResultFields: payload.result
								? Object.keys(payload.result)
								: [],
						});
					}
				} catch {}
			}

			// 401 = token inválido (fetchWithSsoRetry já tentou renovar)
			else if (res.status === 401) {
				try {
					sendToTerminal("⚠️ [REGISTER_EXECUTION:UNAUTHORIZED]", {
						status: res.status,
						error: errorText,
						errorDetails: errorDetails,
						hint: "Token inválido mesmo após retry - verificar SSO",
					});
				} catch {}
			}

			// Outros erros
			else {
				try {
					sendToTerminal("⚠️ [REGISTER_EXECUTION:HTTP_ERROR]", {
						status: res.status,
						statusText: res.statusText,
						error: errorText,
						errorDetails: errorDetails,
					});
				} catch {}
			}

			throw new Error(`HTTP ${res.status}: ${errorText}`);
		}

		// Sucesso - parsing da resposta
		const responseText = await res.text();
		let data: any;

		try {
			data = JSON.parse(responseText);
		} catch {
			// Se não for JSON válido, criar objeto com texto
			data = { rawResponse: responseText };
		}

		try {
			sendToTerminal("✅ [REGISTER_EXECUTION:SUCCESS]", {
				statusCode: res.status,
				logId: data.logId,
				message: data.message,
				requestId: payload.requestId,
				toolSlug: payload.toolSlug,
				responseBody: data,
				responseJSON: JSON.stringify(data, null, 2),
			});
		} catch {}

		return data as RegisterExecutionResult;
	} catch (error) {
		// Log de erro genérico (não bloqueia a aplicação)
		try {
			sendToTerminal("⚠️ [REGISTER_EXECUTION:ERROR]", {
				error: String(error),
				errorName: (error as Error)?.name,
				errorMessage: (error as Error)?.message,
				toolSlug: toolData.slug,
				requestId: response.requestId,
			});
		} catch {}

		return null;
	}
}

/**
 * Registra execução de forma silenciosa (não lança exceções)
 * Útil para fire-and-forget sem bloquear a UI
 *
 * @example
 * ```typescript
 * // Uso fire-and-forget
 * registerExecutionSilent(response, toolData, parameters)
 *   .then(result => logger.debug('Registrado:', result?.logId))
 *   .catch(() => {}); // Ignora erro
 * ```
 */
export async function registerExecutionSilent(
	response: ExecutionResponse,
	toolData: ToolData,
	parameters: Record<string, any>,
): Promise<RegisterExecutionResult | null> {
	try {
		return await registerExecution(response, toolData, parameters);
	} catch {
		return null;
	}
}
